import React, { useEffect, useState } from 'react';
import { navigate, RouteComponentProps } from '@reach/router';
import { ISubject } from './models';
import { AppPath, ERROR_MESSAGE, PUBLIC_SITE_URL } from 'appConstants';
import { getSubjectImage } from 'helpers/getImage';
import Page from 'components/shared/Page';
import { Spinner } from 'components/spinner/Spinner';
import FreeTrialBanner from 'components/freeTrialBanner/FreeTrialBanner';
import SubscriptionRenewalBanner from 'components/subscriptionRenewalBanner/subscriptionRenewalBanner';
import AutoRenewalCancelBanner from 'components/autoRenewalCancelBanner/autoRenewalCancelBanner';
import ErrorMessage from 'components/errorMessage/ErrorMessage';
import { DashboardApi } from './dashboardApi';
import { isBefore, isToday } from 'date-fns';
import { parseISO } from 'date-fns/esm';

const allLessonsOnTrialCheck = (list_info: any) => {
  let allLessonsOnTrial = true;

  for(let i = 0; i < list_info.length; i++){
    if(!list_info[i].isFreeTrial){
      allLessonsOnTrial = false;
    }
  }

  return allLessonsOnTrial;
};

const notOneLessonTrialExpiredCheck = (list_info: any) => {
  let oneLessonTrialExpired = true;
  let prevLessonExpired = false;

  if(list_info.length > 1){
    for(let i = 0; i < list_info.length; i++){
      let expiredFlag = !list_info[i].bExpired ? false : list_info[i].bExpired;

      if(i === 0){
        prevLessonExpired = expiredFlag;
      }
  
      if(i !== 0 && expiredFlag !== prevLessonExpired){
        oneLessonTrialExpired = false;
      }
  
      prevLessonExpired = expiredFlag;
    }
  }

  return oneLessonTrialExpired;
};

export const Dashboard: React.FC<RouteComponentProps> = () => {
  const [token, setToken] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [updateToken, setUpdateToken] = useState<boolean>(true);
  const [updateList, setUpdateList] = useState(true);
  const [dashboardListInfo, setDashboardListInfo] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [allLessonsOnTrial, setAllLessonsOnTrial] = useState<boolean>(false);
  const [notOneLessonTrialExpired, setNotOneLessonTrialExpired] = useState<boolean>(false);
  const [isFreeTrialValid, setIsFreeTrialValid] = useState<any>(false);
  const [isFreeTrialExpired, setIsFreeTrialExpired] = useState<any>(false);
  const [oneTrialOneActive, setOneTrialOneActive] = useState<any>(false);
  const [isMainAccountUser, setIsMainAccountUser] = useState<boolean>(false);
  const [mainAccountHolders, setMainAccountHolders] = useState<any>(null);
  const [renewSubDetails, setRenewSubDetails] = useState<any>([]);
  const [autoRenewCancelDetails, setAutoRenewCancelDetails] = useState<any>([]);
  const [validVATNumber, setValidVATNumber] = useState<boolean>(false);
  const [showVatWarningForSubject, setShowVatWarningForSubject] = useState<string | null>(null);

  const getDashboardListInfo = async () => {
    setLoading(true);

    setRenewSubDetails([]);
    setAutoRenewCancelDetails([]);    

    const result = await DashboardApi.getDashboardList();
    let renewalDays: number | null = null; 
    let renewalSubDetails: any = [];
    let autoRenewCancelDetails: any = [];

    if (result.data) {
      result.data.listSubjects.map((item: any) => {
        const daysDiff = item.daysToRenewal;
        let renewalDetails: any = null;

        /* Find soonest subjects to expiry within 14 days */
        if(daysDiff < 22 && item.bAutoRenew){
          if(
            renewalDays === null || 
            (renewalDays !== null && daysDiff < renewalDays && daysDiff > 0)
          ){
            renewalDetails = {
              renewalDays: daysDiff,
              renewalYears: item.renewalLengthYears,
              accountPackageID: item.accountPackageID,
              subject: item.subject,
              isFreeTrial: item.isFreeTrial,
              bExpired: item.bExpired
            };

            renewalSubDetails.push(renewalDetails);
          }
        }

        /* Find subjects with auto rewewal turned off */
        if(item.bAutoRenew === false){
          autoRenewCancelDetails.push({
            subject: item.subject,
            accountPackageID: item.accountPackageID,
            renewalDays: daysDiff,
            isFreeTrial: item.isFreeTrial,
            autoRenew: item.bAutoRenew,
            renewalDate: item.renewalDate,
          });
        }

        return item;
      });

      if(renewalSubDetails.length > 0){
        setRenewSubDetails(renewalSubDetails);
      }

      if(autoRenewCancelDetails.length > 0){
        setAutoRenewCancelDetails(autoRenewCancelDetails);
      }

      setIsMainAccountUser(result.data.isMainAccountHolder);
      setMainAccountHolders(result.data.mainAccountHolders);
      setValidVATNumber(result.data.bValidVATNumber);
      
      setDashboardListInfo(
        result.data.listSubjects ? result.data.listSubjects : []
      );

      setLoading(false);
    }

    if (result.error) {
      setError(result.error);
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardListInfo();   
  }, []);  

  useEffect(() => {
    if (!updateList) return;
    setUpdateList(false);
  }, [updateList]);

  useEffect(() => {
    if(dashboardListInfo && dashboardListInfo.length){
      setAllLessonsOnTrial(allLessonsOnTrialCheck(dashboardListInfo));
      setNotOneLessonTrialExpired(notOneLessonTrialExpiredCheck(dashboardListInfo));

      const isFreeTrialValidCheck =
        dashboardListInfo &&
        dashboardListInfo.find((i: any) => {
          return i.isFreeTrial && i.trialRemainingDays !== null && i.trialRemainingDays > 0; 
        });

      const isFreeTrialExpiredCheck =
        dashboardListInfo &&
        dashboardListInfo.find((i: any) => {
          return i.isFreeTrial && (i.trialRemainingDays === null || i.trialRemainingDays <= 0);
        });

      let oneLessonExpiredArr: any = [];
      let oneLessonActiveArr: any = [];
      let oneLessonExpiredFunc = (arr: any[]) => arr.some(v => v === true);
      let oneLessonActiveFunc = (arr: any[]) => arr.some(v => v === true);      

      for(let i = 0; i < dashboardListInfo.length; i++){
        oneLessonExpiredArr.push(dashboardListInfo[i].bExpired);
        oneLessonActiveArr.push(dashboardListInfo[i].isFreeTrial === false && dashboardListInfo[i].bExpired === false);
      }

      let oneLessonExpired = oneLessonExpiredFunc(oneLessonExpiredArr);
      let oneLessonActive = oneLessonActiveFunc(oneLessonActiveArr);

      setIsFreeTrialValid(isFreeTrialValidCheck ? isFreeTrialValidCheck : false);
      setIsFreeTrialExpired(isFreeTrialExpiredCheck ? isFreeTrialExpiredCheck : false);
      setOneTrialOneActive(oneLessonExpired && oneLessonActive);     
    }
  }, [dashboardListInfo]);  

  const getFetchAutoLoginToken = async () => {
    const result = await DashboardApi.getAutoLoginToken();
    if (result.data) {
      setToken(result.data.token);
    }
    if (result.error) {
      setToken('');
    }
    setUpdateToken(false);
  };

  useEffect(() => {
    if (!updateToken) return;
    getFetchAutoLoginToken();
  }, [updateToken]);

  const upgradeSubscriptions = () => {
    navigate(AppPath.UPGRADE_SUBSCRIPTION);
  };

  const isTodayOrBeforeExpiryDate = (date: string) => {
    const parsedDate = parseISO(date);
    const isBeforeDate = isBefore(parsedDate, new Date());
    const isTodayDate = isToday(parsedDate);
    return isBeforeDate || isTodayDate;
  };

  const redirectToSubjectSite = (url: string) => {
    const newWindow = window.open(url, '_self', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
    setUpdateToken(true);
  };

  const restartLessonTrial  = async (account_package_id: number) => {
    setLoading(true);

    const result = await DashboardApi.restartTrial(account_package_id);

    if (result.data) {
      getDashboardListInfo();
    }
    if (result.error) {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleNewSubscription = () => {
    navigate(AppPath.NEW_SUBSCRIPTION);
  };

  const handleStartSubscription = (subject: string) => {
    if (!validVATNumber) {
      setShowVatWarningForSubject(subject);
      return;
    }

    setShowVatWarningForSubject(null);
    handleNewSubscription();
  };

  const handleNewFreeTrial = () => {
    navigate(AppPath.NEW_FREE_TRIAL);
  };

  const spinnerHTML = (
    <div className="PageSpinner">
      <Spinner />
    </div>
  );

  const dashboardHTML = (
    <React.Fragment>
      <div className="TitleBar">
        <h1 className="PageTitle">Dashboard</h1>
        {/* <div className="SubscriptionsButtonContainer">
          {accessInfo?.bCanAddFreeTrial && (
            <button
              className="Btn BtnLightOrange NewSubscriptionButton"
              onClick={handleNewFreeTrial}
            >
              <span className="Plus">+</span>
              <span className="Text">Add a free trial</span>
            </button>
          )}
          {accessInfo?.bCanAddSubscription && (
            <button
              className="Btn BtnLightOrange NewFreeTrial"
              onClick={handleNewSubscription}
            >
              <span className="Plus">+</span>
              <span className="Text">Add a new subscription</span>
            </button>
          )}
        </div> */}
      </div>
      {error && <ErrorMessage message={ERROR_MESSAGE} />}
      {dashboardListInfo && dashboardListInfo?.length > 0 ? (
        <div className="GridSection">
          {dashboardListInfo.map((sub: ISubject) => {
            return (
              <div className="GridTile GridImageTile" key={sub.subject}>
                <img
                  src={getSubjectImage(sub.subject)}
                  className="TileImage"
                  alt="Subscription"
                />
                <div className="TileBody">
                  <h2 className="TileTitle">{sub.subject}</h2>
                  {/* A subject the school doesn't have keeps the line, so every tile's buttons line up. */}
                  <p className="TileSubtitle">
                    {sub.package || sub.isFreeTrial ? `${sub.package || ''}${sub.isFreeTrial ? ' Trial' : ''}` : '\u00A0'}
                  </p>

                  {sub.canGetFreeTrial && (
                    <button
                      className="Btn BtnLightOrange NewFreeTrial TileButton"
                      onClick={handleNewFreeTrial}
                    >
                      <span className="Plus">+</span>
                      <span className="Text">Start Free Trial</span>
                    </button>
                  )}

                  {sub.canSubscribe && (
                    <button
                      onClick={() => handleStartSubscription(sub.subject)}
                      className="Btn BtnOrange TileButton"
                    >
                      Start Subscription
                    </button>
                  )}

                  {!sub.canGetFreeTrial && !sub.canSubscribe && (!sub.isFreeTrial || (sub.isFreeTrial && !sub.bExpired && (sub.trialRemainingDays !== null && sub.trialRemainingDays > 0))) && (
                    <button
                      onClick={() =>
                        redirectToSubjectSite(
                          `https://${sub.link}${token ? '?token=' + token : ''}`
                        )
                      }
                      className="Btn BtnOrange TileButton"
                      disabled={sub.bExpired}
                    >
                      Go to {sub.subject}
                    </button>
                  )}

                  {/*sub.isFreeTrial && (sub.trialRemainingDays === null || sub.trialRemainingDays <= 0) && (
                    <button
                      onClick={upgradeSubscriptions}
                      className="Btn BtnOrange TileButton"
                    >
                      Complete subscription payment
                    </button>
                  )*/}


                  {sub.isFreeTrial && sub.bExpired && (sub.trialRemainingDays === null || sub.trialRemainingDays <= 0)  && (
                    <button
                      onClick={() => restartLessonTrial(sub.accountPackageID)}
                      className="Btn BtnLightOrange TileButton"
                    >
                      Restart trial
                    </button>                  
                  )}                  

                  {(sub.isFreeTrial) && (!sub.bExpired || (sub.bExpired && (sub.trialRemainingDays === null || sub.trialRemainingDays <= 0) )) && (
                    <button
                      onClick={upgradeSubscriptions}
                      className="Btn BtnLightOrange TileButton"
                    >
                      Upgrade to subscription
                    </button>                  
                  )}

                  {!sub.isFreeTrial &&
                    sub.bExpired &&
                    isTodayOrBeforeExpiryDate(sub.renewalDate) && (
                      //when the subscription is expired
                      <button
                        onClick={() => navigate(AppPath.MANAGESCHOOL)}
                        className="Btn BtnOrange TileButton"
                      >
                        Renew Subscription
                      </button>
                    )}
                    
                  {!sub.bExpired && sub.isFreeTrial && !sub.bMarkSubscriptionAsPaid && (
                      //when account has been created buy paying the subscription by invoice and the subscription wasn't been activated yet
                      <p>
                        Someone from our team will notify you when your trial has been activated.
                      </p>
                    )}
                  {!sub.bExpired && !sub.isFreeTrial && !sub.bMarkSubscriptionAsPaid && sub.accountPackageID && (
                      //when subscribed for a paid package where the admin has not activated the account
                      <p>
                        Someone from our team will notify you when your subscription has been activated.
                      </p>
                    )}

                  {showVatWarningForSubject === sub.subject && !validVATNumber && (
                    <p>
                      If you do not have a VAT number, please <a href={`${PUBLIC_SITE_URL}/contact-us`} target="_blank" rel="noreferrer">contact us</a> before purchasing so we can verify your organisation.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="MsgContainer">
          <div className="Txt">No subscriptions</div>
        </div>
      )}
    </React.Fragment>
  );

  return (
    <Page>
      {(isFreeTrialExpired || isFreeTrialValid) && !oneTrialOneActive && (
        <FreeTrialBanner
          daysLeft={isFreeTrialValid?.trialRemainingDays ?? null}
          upgradeSubscriptions={upgradeSubscriptions}
          isFreeTrialValid={!!isFreeTrialValid}
          dashboardListInfo={dashboardListInfo}
        />
      )} 

      {isMainAccountUser &&
        <>
          {renewSubDetails && renewSubDetails.length > 0 && 
            <SubscriptionRenewalBanner 
              renewSubDetails={renewSubDetails}
              isMainAccountUser={isMainAccountUser}
              mainAccountHolders={mainAccountHolders}
              setError={setError}
            />  
          }
          {autoRenewCancelDetails && autoRenewCancelDetails.length > 0 &&
            <AutoRenewalCancelBanner 
              autoRenewCancelDetails={autoRenewCancelDetails}
              getDashboardListInfo={getDashboardListInfo}
              setError={setError}
            />   
          }        
        </>
      }
      
      <div className="PageContent DashboardPage WidthContent">
        {loading ? spinnerHTML :
          <>
            {dashboardListInfo || error ? dashboardHTML : spinnerHTML}
          </>
        }
      </div>
    </Page>
  );
};
